import { ReferralSchema } from '@/models/api/referral'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import superagent from 'superagent'

import { log } from '@/log'
import { CommerceSalesOrder } from '@/models/api/commerce-sales-order'
import { convertLongIdToDisplayId } from '@/models/api/id'
import { SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { CartScopeEq } from '@/models/cart/group/scope'
import { DateWithoutHour } from '@/models/date-time/date-without-hour'
import { OrderKind } from '@/models/order/order'
import ParametersBuilder from '@/services/payment/parameters-builder'
import { DateFormat } from '@/util/format'
import { toQuery } from '@spa/components/pages/order/scope-transpiler'

import { CartItem } from '../store/modules/cart/types'
import {
  Delivery,
  Payment,
} from '../store/modules/orderConfirm/types'

import { PurchaseCreateResult, PurchaseService } from './api/purchase'
import { SellerSalesOrder } from './api/sellerSalesOrder'
import adjustSetItems from './order/cartItemsManipulator'
import { createPaymentParamBuilder } from './order/paymentParamBuilder'

export const kind: { [key: string]: OrderKind } = {
  Standard: 'standard',
  PreOrder: 'preorder',
  Catalog: 'catalog',
}

export interface Order {
  id?: string,
  deliveryWhen: string | null
  cartItems: CartItem[]
  kind: OrderKind
  discount?: OrderDiscount
  estimatedPoints?: number
}

export interface OrderDiscount {
  coupon?: number
  bundle?: number
}

export interface ErrorResponseBody {
  cause: string[]
  error_at: string
  timeout?: boolean
}
export type ErrorResponse = Error & {
  response: null | {
    status: number
    body: ErrorResponseBody
  }
}
export interface OrderPurchaseResult {
  success: boolean
  order: Order
  error: ErrorResponse | null
  redirectHtml?: string
}

export function extractFrontErrorBodiesFromPurchaseResult(results: OrderPurchaseResult[]): ErrorResponseBody[] {
  const errorBodies: ErrorResponseBody[] = _.compact(
    results.map(
      r => _.get(r.error, ['response', 'body'], null)
    )
  )

  return errorBodies.filter(e => e.error_at === 'front')
}

/**
 * 決済APIの実行結果がタイムアウトかどうかを判定する。
 * ステータスコードにより、あるいはレスポンスボディにfonrtで付与したtimeoutフィールド = true で判定する。
 * cf. https://github.com/my-color/front/issues/5511#issuecomment-892075725
 */
export function timeoutErrorExistsInPurchaseResult(results: OrderPurchaseResult[]): boolean {
  return results.some((r) => {
    const response: {
      status: number,
      body: ErrorResponseBody
    } | null = _.get(r.error, ['response'], null)

    if (!response) {
      return false
    }

    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/504
    const HTTP_STATUS_GATEWAY_TIMEOUT = 504
    const timeoutStatusCodes = [HTTP_STATUS_GATEWAY_TIMEOUT]

    return timeoutStatusCodes.includes(response.status) ||
      response.body.timeout
  })
}

export interface OrderPurchaseSuccess extends OrderPurchaseResult {
  success: true
  error: null
}
export interface OrderPurchaseFail extends OrderPurchaseResult {
  success: false
  error: ErrorResponse
  redirectHtml: undefined
}

export class OrderList {
  constructor(readonly list: Order[]) {}

  get hasMultiple(): boolean {
    return this.list.length > 1
  }

  get hasPreOrder(): boolean {
    return this.list.some(order => order.kind === 'preorder')
  }
}

export class OrderService {
  static readonly kind: { [key: string]: OrderKind } = {
    standard: 'standard',
    preorder: 'preorder',
  }

  static create({ appId, orderSource }: { appId?: string, orderSource: OrderSource }) {
    return new OrderService(orderSource, appId)
  }

  private purchaseService: PurchaseService
  private sellerSalesOrder: SellerSalesOrder

  private constructor(
    private readonly orderSource: OrderSource,
    appId?: string
  ) {
    this.purchaseService = new PurchaseService(appId)

    // Always use host appid, for a sales order is created under host-scope
    this.sellerSalesOrder = new SellerSalesOrder()
  }

  /**
   * カート内商品の内容をベースに、通常注文・予約注文・カタログ注文に変換・選り分け
   *
   * @note サーバー CartItemList#toBePartitionedByMultipleOrders と機能的に重複している。
   */
  createOrderFromCartItems(
    cartItems: CartItem[],
    deliveryDateForStandardOrder: string
  ): Order[] {
    const { preSalesItems, standardSaleItems } = this.groupCartItemsByPreSalesOrNot(cartItems)

    const preOrders: Order[] = this.convertCartItemsToPreOreder(preSalesItems)

    if (standardSaleItems.length === 0) {
      return preOrders
    }

    const { catalog, standard } = this.groupCartItemsByCatalogOrNot(standardSaleItems)

    /**
     * この並び順は見積もりの並び順と揃える必要があることに注意！
     */
    return _.compact([
      standard.length > 0 ? {
        deliveryWhen: deliveryDateForStandardOrder,
        // ノベルティは後ろに来るように並び替え
        cartItems: standard.sort((a, b) => (a.isNovelty ? 1 : 0) - (b.isNovelty ? 1 : 0)),
        kind: kind.Standard,
      } : null,
      catalog.length > 0 ? {
        deliveryWhen: '指定不可',
        cartItems: catalog,
        kind: kind.Catalog,
      } : null,
      ...preOrders,
    ])
  }

  async purchase(
    params: PurchaseParams,
    parallel: boolean = true,
    beforePaymentAction: (() => Promise<Either<Error, Payment>>) | null = null
  ): Promise<OrderPurchaseResult[]> {
    if (parallel) {
      return this.purchaseParallel(params)
    }

    return this.purchaseSeries(params, beforePaymentAction)
  }

  private async purchaseParallel(params: PurchaseParams): Promise<OrderPurchaseResult[]> {
    const { orders, ...otherParams } = params

    const purchaseRequests = orders.map(async (order: Order) => {
      return this.purchaseSingle({
        ...otherParams,
        order,
      })
    })

    return await Promise.all(purchaseRequests)
  }

  private async purchaseSeries(
    params: PurchaseParams,
    // nullじゃなくて恒等関数にする
    beforePaymentAction: (() => Promise<Either<Error, Payment>>) | null = null
  ): Promise<OrderPurchaseResult[]> {
    const { orders, payment, ...otherParams } = params
    // ここなぜlet むしろいいやつ？（変更されることへの注意）
    let purchaseResult: OrderPurchaseResult[] = []

    // もっと簡単にかけるやつない？
    for (const order of orders) {
      let paymentPerOrder: Payment = payment

      if (beforePaymentAction) {
        // hookResult
        // hookにpayment引数を明示的にここで渡して差分追加したものをかえしたい。デコレータ
        const paymentResult = await beforePaymentAction()
        if (paymentResult.isLeft()) {
          const errorResult = {
            success: false,
            order,
            // error: paymentResult.value で良さそう。使っているところを確認
            error: {
              response: null,
              // Errorをspread演算子で展開していると捨てられる情報がありそう
              ...paymentResult.value,
            },
          }
          purchaseResult = purchaseResult.concat(errorResult)
          // log.error出す
          break
        }
        /**
         * beforePaymentAction で支払い方法に特化したパラメータを生成して交換している.
         * beforePaymentAction が使用された場合、元の payment パラメータは全て破棄されてしまう.
         *
         * しかし point など、捨てられてはいけないパラメータも存在する.
         * そうした値は、別途付与し直している.
         *
         * FIXME: 交換されて良い値と保持されなければいけない値の区別がつかない
         * FIXME: そもそもとして、渡された値を後からまるごと交換するような形を取りたくない
         * パラメータを渡すか、パラメータを生成する関数を渡すか、どちらか二択にした方が好ましいのではないか.
         * 一旦は、影響範囲もあるので既存実装の構造に乗るが、放置すると、
         * 今後支払い方法のバリエーションが増えてきた時にどんどんメンテナンスや問題調査が困難になっていく危険がある.
         *
         * @see https://github.com/my-color/front/issues/5068
         */
        paymentPerOrder = paymentResult.value
        paymentPerOrder.point = payment.point
      }

      const result = await this.purchaseSingle({
        order,
        payment: paymentPerOrder,
        ...otherParams,
      })
      purchaseResult = purchaseResult.concat(result)
      if (!result.success) {
        // log.error
        // エラー時にpurchaseResultに詰めたい
        break
      }
    }

    return purchaseResult
  }

  private async purchaseSingle(params: SinglePurchaseParams): Promise<OrderPurchaseResult> {
    const response = await this.purchaseSingleImpl(params)

    return this.makePurchaseResult(response, params.order)
  }

  private async purchaseSingleImpl(params: SinglePurchaseParams): Promise<Either<Error, superagent.Response>> {
    // Provisional ID
    let provisionalSalesOrderId = params.order.id

    if (!provisionalSalesOrderId) {
      const provisionalOrderIdResult = await this.createProvisionalOrderId()
      if (provisionalOrderIdResult.isLeft()) {
        return new Left(provisionalOrderIdResult.value)
      }

      provisionalSalesOrderId = provisionalOrderIdResult.value
    }

    // Check PreOrder
    const isPreOrder = params.order.kind === kind.PreOrder

    if (isPreOrder) {
      // https://github.com/my-color/front/issues/3392
      // 予約注文ではクーポン無視 & 適用しない
      return this.purchasePreOrder({
        provisionalSalesOrderId,
        order: params.order,
        payment: params.payment,
        delivery: params.delivery,
        deliveryDate: null, // 予約注文の場合は配送日指定不可
        deliveryTimeSlot: null, // 予約注文の場合は配送日指定不可
        unattendedDelivery: params.unattendedDelivery,
        adCode: params.adCode,
        bundleDiscountPrice: params.bundleDiscountPrice,
        couponDiscountPrice: params.couponDiscountPrice,
        cartScope: params.cartScope,
        serviceFee: params.serviceFee,
        referral: params.referral,
      })
    }

    /**
     * :pensive:
     * https://github.com/my-color/front/issues/2316
     */
    if (params.order.kind === kind.Catalog) {
      // https://github.com/my-color/front/issues/3860#issuecomment-778927965
      // カタログ商品ではポイント適用不可
      return this.purchaseStandardOrder({
        provisionalSalesOrderId,
        order: params.order,
        payment: {
          ...params.payment,
          point: undefined,
        },
        delivery: params.delivery,
        deliveryDate: null, // カタログでは配送日指定不可
        deliveryTimeSlot: null, // カタログでは配送日指定不可
        unattendedDelivery: params.unattendedDelivery,
        adCode: params.adCode,
        couponId: params.couponId,
        discount: params.discount,
        bundleDiscountPrice: params.bundleDiscountPrice,
        couponDiscountPrice: params.couponDiscountPrice,
        cartScope: params.cartScope,
        serviceFee: params.serviceFee,
        referral: params.referral,
      })
    }

    return this.purchaseStandardOrder({
      provisionalSalesOrderId,
      order: params.order,
      payment: params.payment,
      delivery: params.delivery,
      deliveryDate: params.deliveryDate,
      deliveryTimeSlot: params.deliveryTimeSlot,
      unattendedDelivery: params.unattendedDelivery,
      adCode: params.adCode,
      couponId: params.couponId,
      discount: params.discount,
      bundleDiscountPrice: params.bundleDiscountPrice,
      couponDiscountPrice: params.couponDiscountPrice,
      cartScope: params.cartScope,
      serviceFee: params.serviceFee,
      referral: params.referral,
    })
  }

  private createPayloadForOrder(params: SingleOrderParams): Either<Error, PurchasePayload> {
    const displayOrderId = convertLongIdToDisplayId(params.provisionalSalesOrderId)

    const {
      provisionalSalesOrderId,
      order,
      payment,
      delivery,
      deliveryDate,
      deliveryTimeSlot,
      cartScope,
      unattendedDelivery,
    } = params

    payment.variableResource = {
      ...payment.variableResource,
      ...{ orderDisplayId: displayOrderId },
    }
    const makePaymentParamResult = this.makePaymentParam(payment)
    if (makePaymentParamResult.isLeft()) {
      return new Left(makePaymentParamResult.value)
    }

    return new Right({
      sales_order_id: provisionalSalesOrderId,
      cart_ids: order.cartItems.map(i => i.id).join(','),
      payment: makePaymentParamResult.value,
      delivery: this.makeDeliveryParam({
        ...delivery,
        deliveryDate,
        deliveryTimeSlot,
        unattendedDelivery,
      }),
      ...params.adCode && { memo: params.adCode },
      ...order.discount && { aux_long_2: order.discount.bundle },
      ...order.discount && { aux_long_3: order.discount.coupon },
      ...order.estimatedPoints && { aux_long_4: order.estimatedPoints },
      aux_string_3: displayOrderId,
      ...params.couponId && { aux_string_5: params.couponId },
      aux_string_9: toQuery(cartScope),
      ...params.referral && { aux_string_13: JSON.stringify(params.referral) },
      ...this.orderSource.fold(
        () => ({}),
        value => ({ aux_string_14: value })
      ),
      ...params.discount && { discount: params.discount },
      ...params.serviceFee && { service_fee: JSON.stringify(params.serviceFee) },
    })
  }

  private async purchaseStandardOrder(
    params: SingleOrderParams
  ): Promise<Either<Error, superagent.Response>> {
    const payload = this.createPayloadForOrder(params)
    if (payload.isLeft()) {
      return new Left(new Error('Error on creating payload.'))
    }

    return this.purchaseService.create(payload.value)
  }

  private async purchasePreOrder(
    params: SingleOrderParams
  ): Promise<Either<Error, superagent.Response>> {
    const payload = this.createPayloadForOrder(params)
    if (payload.isLeft()) {
      return new Left(new Error('Error on creating payload.'))
    }

    const purchaseResult = await this.purchaseService.create(payload.value)

    if (purchaseResult.isLeft()) {
      return purchaseResult
    }

    /**
     * 予約注文を決済後、その注文が「予約注文」であることを示すタグを付与する
     *
     * TODO: 将来的にはEngineで吸収される想定（2019/02頃？）.対応後、以下のタグ付与処理を削除.
     */
    const purchaseResponse = purchaseResult.value.body.data as PurchaseCreateResult
    const sellerSalesOrderIds = purchaseResponse.salesOrders.map(s => s.sales_order_id)

    await this.markAsPreOrder(sellerSalesOrderIds)

    // 注文後の予約注文タグ付けが失敗しても、表面上は支障無く処理完了したように見せる.
    return purchaseResult
  }
  private markAsPreOrder(salesOrderIds: string[]) {
    return Promise.all(salesOrderIds.map(salesOrderId => this.sellerSalesOrder.appendTag(
      salesOrderId,
      [
        'mycolor.salesorder.preorder',
      ]
    )))
  }

  private makeDeliveryParam(
    delivery: {
      deliveryType: string,
      id: string,
      deliveryMethod: string,
      deliveryDate: DateWithoutHour | null,
      deliveryTimeSlot: string | null,
      unattendedDelivery: string | null,
    }
  ) {
    const { deliveryType, id, deliveryMethod, deliveryDate, deliveryTimeSlot, unattendedDelivery } = delivery

    const param = _.merge(
      {
        delivery_type: deliveryType,
        id,
        delivery_method: deliveryMethod,
      },
      deliveryDate ? {
        date: DateFormat.yyyyMmDd(deliveryDate.valueWithNormalizedHour),
      } : {},
      deliveryTimeSlot ? {
        timeSlot: deliveryTimeSlot,
      } : {},
      unattendedDelivery ? { unattended_delivery: unattendedDelivery } : {}
    )

    return JSON.stringify(param)
  }

  private makePaymentParam(payment: Payment): Either<Error, string> {
    if (!payment.apiPayment || payment.apiPayment.parameters.isEmpty) {
      return new Right(
        this.makePaymentParamOld(payment)
      )
    }

    // サーバーで定義された決済方法オブジェクトに従ってペイロードを構築する
    const result = ParametersBuilder.create(
      payment.apiPayment.parameters,
      payment.selectedOptionList || SelectOptionList.empty(),
      payment.variableResource || {}
    ).build()
    if (result.isLeft()) {
      const message = 'Cannot build parameters for commerce_purchase.'
      log.error({
        message,
        errors: result.value,
      })

      return new Left(new Error(message))
    }

    // ポイントはすべての決済方法に関して付与可能なので最後に結合させる
    const { point } = payment

    return new Right(JSON.stringify({
      ...(result.value),
      point,
    }))
  }

  /**
   * サーバーで定義された決済方法オブジェクトに従わないルート
   * 段階的に廃止する予定
   */
  private makePaymentParamOld(payment: Payment): string {
    return createPaymentParamBuilder(payment).build()
  }

  private makePurchaseResult(response: Either<Error, superagent.Response>, order: Order): OrderPurchaseResult {
    if (response.isLeft()) {
      return {
        success: false,
        order,
        error: (response.value as ErrorResponse),
      }
    }

    const redirectHtmlList = _.compact(_.map(
      _.get(response.value.body, 'data.salesOrders') as any[],
      (raw: any) => (new CommerceSalesOrder(raw)).redirectHtml
    ))
    if (redirectHtmlList.length > 1) {
      log.error('Two or more redirectHtml found. All but the first element will be ignored.')
    }

    return {
      success: true,
      order: {
        // このメソッドは単一注文であることが前提になっているため、結果配列内の要素もただ一つであることを前提とする
        id: _.get(response.value.body, 'data.salesOrders[0].sales_order_id'),
        ...order,
      },
      error: null,
      redirectHtml: redirectHtmlList[0] || undefined,
    }
  }

  private convertCartItemsToPreOreder(cartItems: CartItem[]): Order[] {
    return _.toPairs(this.groupPreSalesItemsByWhenSalesStart(cartItems)).map((pair) => {
      const [deliveryWhen, groupedCartItems] = pair

      return {
        deliveryWhen,
        cartItems: groupedCartItems,
        kind: kind.PreOrder,
      }
    })
  }

  private groupCartItemsByPreSalesOrNot(cartItems: CartItem[]) {
    const editedCartItems = adjustSetItems(cartItems)

    const [preSalesItems, standardSaleItems] = _.partition(editedCartItems, item => item.isPreSales)

    return {
      preSalesItems,
      standardSaleItems,
    }
  }
  private groupCartItemsByCatalogOrNot(cartItems: CartItem[]) {
    const [catalog, standard] = _.partition(cartItems, item => item.isCatalogType)

    return {
      catalog,
      standard,
    }
  }

  private groupPreSalesItemsByWhenSalesStart(preSalesItems: CartItem[]) {
    return _.groupBy(preSalesItems, preSales => DateFormat.partOfMonth(preSales.salesStartAt))
  }

  private async createProvisionalOrderId(): Promise<Either<Error, string>> {
    // Always use host appid, for a sales order is created under host-scope,
    // while /commerce_purchase is executed under brand-scope to check coupon, novelty etc.
    return new PurchaseService().createProvisionalOrderId()
  }
}

interface PurchaseParams {
  provisionalSalesOrderId?: string,
  orders: Order[],
  payment: Payment,
  delivery: Delivery,
  deliveryDate: DateWithoutHour | null,
  deliveryTimeSlot: string | null,
  unattendedDelivery: string | null,
  adCode?: string | undefined,
  couponId?: string,
  // TODO: When the responsibility of coupon checking is moved to AppLogic, discount paremeter will be removed.
  discount?: number,
  bundleDiscountPrice?: number,
  couponDiscountPrice?: number,
  cartScope: CartScopeEq,
  /**
   * クライアントで表示しているサービス手数料と、サーバーで実際に適用されるサービス手数料が一致するか検証する必要がある。
   * もしサービス手数料が一致しない場合はレジ画面の再読み込みを行う。
   * ただし、現段階 (2022/05) ではサービス手数料に関しては暫定的な手当てが行われており、
   * APIでどの値を受け渡しするべきか明確に定められない。
   * そのため、この段階ではConsoleで変更できるサービス手数料の割合が変化していないことのみ検証することとする。
   * @see https://github.com/my-color/front/issues/6325#issuecomment-1119534397
   */
  serviceFee?: {
    percent: number,
  },
  referral: ReferralSchema | null
}

type SinglePurchaseParams = Omit<PurchaseParams, 'orders'> & {
  order: Order,
}

type SingleOrderParams = Omit<SinglePurchaseParams, 'provisionalSalesOrderId'> & {
  provisionalSalesOrderId: string
}

/**
 * 注文情報
 * この決済用の注文情報の型にフィールドを増やした場合、注文履歴の型にもフィールドを増やす必要があるか、確認することが望ましい。
 * @see import('/client/spa/src/store/modules/orderHistory/types').Order
 */
interface PurchasePayload {
  sales_order_id: string,
  cart_ids: string,
  payment: string,
  delivery: string,
  memo?: string,
  aux_long_2?: number, // バンドル割引額
  aux_long_3?: number, // クーポン割引額
  aux_long_4?: number, // 付与予定ポイント
  aux_string_3: string,
  aux_string_5?: string,  // coupon id
  /**
   * クエリ文字列形式のカートスコープ (scope_kind.scope_value)
   * @see https://my-color.esa.io/posts/514
   */
  aux_string_9: string,
  /**
   * 招待情報
   * @see https://github.com/Everforth/growthpalette-pm/issues/304#issuecomment-2443116320
   */
  aux_string_13?: string,
  aux_string_14?: string, // 注文の経路 (orderSource)
  // TODO: When the responsibility of coupon checking is moved to AppLogic, discount paremeter will be removed.
  discount?: number,
  service_fee?: string,
}

/**
 * どこから来て注文が発生したかを表す （GA4におけるsourceのイメージを借用している）
 * 2025/06時点では、ホテラバアプリからの注文の場合のみ値が入る
 */
export class OrderSource {
  static asUndefined(): OrderSource {
    return new OrderSource(null)
  }
  static valueOf(value: OrderSourceValue): OrderSource {
    return new OrderSource(value)
  }
  static createFromNullable(value: string | null): OrderSource {
    return value === null ? this.asUndefined() : this.valueOf(value as OrderSourceValue)
  }

  private constructor(private readonly value: OrderSourceValue | null) {}

  fold<T>(
    onUndefined: () => T,
    onDefined: (value: OrderSourceValue) => T
  ): T {
    return this.value === null ? onUndefined() : onDefined(this.value)
  }
}
type OrderSourceValue = 'hlapp'
